import { INNER_BORDER_COLOR, OUTER_BORDER_COLOR } from "../../styles/colors";
import {
  COSTPER_NUMBER_FORMAT,
  TOTAL_NUMBER_FORMAT,
  allEdges,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { WORKSHEET_CUSTOM_PROPERTIES, getTemplateByReportTypeOrNull } from "../../workbook/customProperties";
import { templateSheetName } from "../../workbook/tableNames";
import { ROOMS_REPORT_NAME } from "../api/reportNames";
import { rowNames } from "../helpers/rangeNames";
import { generateRoomsReports } from "../rooms";
import { titleMaker } from "./common";

const buildRoomsTemplate = async (context: Excel.RequestContext, reportTitle: string) => {
  const workbook = context.workbook;
  const templateWorksheet = workbook.worksheets.add(templateSheetName(reportTitle));
  await context.sync();

  await titleMaker(templateWorksheet, reportTitle);

  const rowHeights = [12, 30, 25, 25, 12, 25];
  rowHeights.forEach((height, index) => {
    templateWorksheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
  });

  // Column widths: A-S for rooms, finishes section
  // A: spacer, B: room name, C: perimeter, D: area avg, E: qty, F: height, G: area total, H: total cost
  // I: spacer, J: walls, K: floor, L: ceiling, M: base, N: spacer, O: mark, P: description, Q: qty, R: uom, S: unit cost
  const columnWidths = [20, 180, 70, 70, 50, 50, 80, 90, 12, 100, 100, 100, 100, 12, 60, 180, 70, 50, 70];
  columnWidths.forEach((width, index) => {
    templateWorksheet.getRange(
      `${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`
    ).format.columnWidth = width;
  });

  templateWorksheet.getRange("1:10").format.verticalAlignment = Excel.VerticalAlignment.center;
  await context.sync();

  const headers: rangeFormatting[] = [
    // Room section
    { rangeAddress: "B6", value: "Room Name", name: "\\room_name", Halignment: "Left" },
    { rangeAddress: "C6", value: "Perimeter", name: "\\perimeter", Halignment: "Center" },
    { rangeAddress: "D6", value: "Area Avg.", name: "\\area_avg", Halignment: "Center" },
    { rangeAddress: "E6", value: "QT", name: "\\room_qty", Halignment: "Center" },
    { rangeAddress: "F6", value: "Height", name: "\\room_height", Halignment: "Center" },
    { rangeAddress: "G6", value: "Area Total", name: "\\area_total", Halignment: "Center" },
    { rangeAddress: "H6", value: "Total Cost", name: "\\total_cost", Halignment: "Center" },
    // Finishes section
    { rangeAddress: "J6", value: "Walls", name: "\\walls", Halignment: "Center" },
    { rangeAddress: "K6", value: "Floor", name: "\\floor_finish", Halignment: "Center" },
    { rangeAddress: "L6", value: "Ceiling", name: "\\ceiling", Halignment: "Center" },
    { rangeAddress: "M6", value: "Base", name: "\\base", Halignment: "Center" },
    // Finish summary section
    { rangeAddress: "O6", value: "Mark", name: "\\mark", Halignment: "Center" },
    { rangeAddress: "P6", value: "Description", name: "\\description", Halignment: "Left" },
    { rangeAddress: "Q6", value: "Qt", name: "\\finish_qty", Halignment: "Center" },
    { rangeAddress: "R6", value: "UOM", name: "\\finish_uom", Halignment: "Center" },
    { rangeAddress: "S6", value: "Unit Cost", name: "\\finish_unit_cost", Halignment: "Center" },
  ];
  headers.forEach((header) => {
    header.fontSize = 12;
    header.bold = true;
  });
  applyRangeFormats(templateWorksheet, headers);

  const dataRows: rangeFormatting[] = [
    { rangeAddress: "B7:S8", name: rowNames.roomItems },
    // Room metrics formatting
    { rangeAddress: "C7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "D7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "E7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "F7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "G7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "H7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    // Finish summary formatting
    { rangeAddress: "O7", Halignment: "Center" },
    { rangeAddress: "P7", Halignment: "Left" },
    { rangeAddress: "Q7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
    { rangeAddress: "R7", Halignment: "Center" },
    { rangeAddress: "S7", numberFormat: COSTPER_NUMBER_FORMAT, Halignment: "Right" },
  ];
  applyRangeFormats(templateWorksheet, dataRows);

  // Grid styling - Room section
  applyGridStyle({
    range: templateWorksheet.getRange("B7:H8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("A7:A8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });
  applyGridStyle({
    range: templateWorksheet.getRange("H7:H8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  // Grid styling - Finishes section
  applyGridStyle({
    range: templateWorksheet.getRange("J7:M8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("I7:I8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });
  applyGridStyle({
    range: templateWorksheet.getRange("M7:M8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  // Grid styling - Finish summary section
  applyGridStyle({
    range: templateWorksheet.getRange("O7:S8"),
    borderColor: INNER_BORDER_COLOR,
    weight: "Thin",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("N7:N8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });
  applyGridStyle({
    range: templateWorksheet.getRange("S7:S8"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: ["EdgeRight"],
  });

  // Header styling
  applyGridStyle({
    range: templateWorksheet.getRange("B6:H6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("J6:M6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });
  applyGridStyle({
    range: templateWorksheet.getRange("O6:S6"),
    borderColor: OUTER_BORDER_COLOR,
    weight: "Medium",
    edges: allEdges,
  });

  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE, reportTitle);
  templateWorksheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE, "true");
  await context.sync();

  templateWorksheet.showGridlines = false;
  templateWorksheet.visibility = Excel.SheetVisibility.hidden;
  await context.sync();

  return templateWorksheet;
};

export const generateRoomsReportsFromTemplate = async () => {
  return Excel.run(async (context) => {
    const reportTitle = ROOMS_REPORT_NAME;
    const template =
      (await getTemplateByReportTypeOrNull(context, reportTitle)) || (await buildRoomsTemplate(context, reportTitle));
    await generateRoomsReports(context, template);
  });
};
